import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, CheckIcon, LockKeyholeIcon, MailIcon } from 'lucide-react';
import { ButtonLink } from '../components/ui/ButtonLink';
import { PageHero } from '../components/ui/PageHero';
import { consultants } from '../data/consultants';
import { opportunities } from '../data/opportunities';
import { usePageMeta } from '../hooks/usePageMeta';
export function OpportunityDetail() {
  const {
    slug
  } = useParams();
  const opportunity = opportunities.find(o => o.slug === slug);
  const consultant = opportunity ? consultants.find(c => c.slug === opportunity.consultantSlug) : undefined;
  usePageMeta(opportunity ? opportunity.title : 'Opportunity not available', opportunity ? opportunity.summary : 'This leadership opportunity is not currently available. Register your interest for future executive search assignments.');
  if (!opportunity) {
    return <>
        <PageHero compact label="Leadership Opportunities" title="This opportunity is not available" intro="It may have been filled, withdrawn, or never published. You can still register your interest for relevant future assignments." data-id="element-949" data-mp="2wjzl8t6trfz7">
          <ButtonLink to="/candidates#introduce" size="lg" data-id="element-950" data-mp="gn0ovbk1o3hw">
            Register Your Interest
          </ButtonLink>
          <ButtonLink to="/opportunities" variant="outline-light" size="lg" data-id="element-951" data-mp="llk9n8mwj98o">
            Back to Opportunities
          </ButtonLink>
        </PageHero>
      </>;
  }
  return <>
      <PageHero compact label={`${opportunity.seniority} · ${opportunity.industry}`} title={opportunity.title} intro={opportunity.confidential ? 'Confidential organization' : opportunity.organization} data-id="element-952" data-mp="3p2ke7zhlh1ro" />
      <section className="bg-porcelain py-16 lg:py-24" data-id="element-953" data-mp="2o11s8c6jwnu">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-12 lg:px-10" data-id="element-954" data-mp="2pki1yxu67pm">
          <div className="space-y-12 lg:col-span-7" data-id="element-955" data-mp="31bn9p7s4p6qg">
            <Link to="/opportunities" className="inline-flex items-center gap-1.5 text-sm font-semibold text-plum/70 hover:text-copper-deep" data-id="element-956" data-mp="36c2q687piucn">
              <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-957" data-mp="2ya211vi7ioo2" />
              All opportunities
            </Link>
            {opportunity.confidential && <p className="flex gap-3 rounded-xl bg-limestone p-5 text-sm leading-relaxed text-plum/80" data-id="element-958" data-mp="380wqf8qr24dq">
                <LockKeyholeIcon className="mt-0.5 h-4 w-4 shrink-0 text-copper-deep" aria-hidden="true" data-id="element-959" data-mp="33467mdnk8uom" />
                This is a confidential search. Further details are shared in conversation with suitable candidates.
              </p>}
            <p className="max-w-prose text-lg leading-relaxed text-plum/75" data-id="element-960" data-mp="2pb6uujf5xekr">{opportunity.summary}</p>
            <section data-id="element-961" data-mp="2kcn7cekx7uqa">
              <h2 className="text-2xl font-semibold text-aubergine" data-id="element-962" data-mp="2kc3xll5fccnc">Key responsibilities</h2>
              <ul className="mt-5 space-y-3" data-id="element-963" data-mp="4kg5c31cv8av">
                {opportunity.responsibilities.map(r => <li key={r} className="flex gap-3 leading-relaxed text-plum/75" data-id="element-964" data-mp="3vqh7tn798tkw">
                    <CheckIcon className="mt-1 h-4 w-4 shrink-0 text-copper" aria-hidden="true" data-id="element-965" data-mp="1hj9laggi87a3" />
                    {r}
                  </li>)}
              </ul>
            </section>
            <section data-id="element-966" data-mp="18jmhjo0aypdv">
              <h2 className="text-2xl font-semibold text-aubergine" data-id="element-967" data-mp="18kefv6lab17u">Candidate profile</h2>
              <ul className="mt-5 space-y-3" data-id="element-968" data-mp="1dj7jm7arrdut">
                {opportunity.profile.map(p => <li key={p} className="flex gap-3 leading-relaxed text-plum/75" data-id="element-969" data-mp="218tg2mzrbez4">
                    <CheckIcon className="mt-1 h-4 w-4 shrink-0 text-copper" aria-hidden="true" data-id="element-970" data-mp="259vh99d8i13q" />
                    {p}
                  </li>)}
              </ul>
            </section>
          </div>
          <aside className="lg:col-span-4 lg:col-start-9" data-id="element-971" data-mp="1wg0do5rrgiq7">
            <div className="rounded-2xl border border-plum/10 bg-white p-6 lg:sticky lg:top-28" data-id="element-972" data-mp="2k6mmhz9la719">
              <dl className="space-y-4 text-sm" data-id="element-973" data-mp="2oxcqp96a43ud">
                <div data-id="element-974" data-mp="2oy4kqfk89tij">
                  <dt className="text-plum/55" data-id="element-975" data-mp="2oynw1dfdhjnl">Location</dt>
                  <dd className="mt-0.5 font-medium text-plum" data-id="element-976" data-mp="10cl49sh89w6t">{opportunity.location}</dd>
                </div>
                <div data-id="element-977" data-mp="vbnofmjoxe8h">
                  <dt className="text-plum/55" data-id="element-978" data-mp="vb45c2ib0jsv">Function</dt>
                  <dd className="mt-0.5 font-medium text-plum" data-id="element-979" data-mp="ra25pkkh65qd">{opportunity.leadershipFunction}</dd>
                </div>
              </dl>
              <ButtonLink to="/candidates#introduce" className="mt-6 w-full" data-id="element-980" data-mp="mdleghhzbb92">
                Express Your Interest
              </ButtonLink>
              {consultant && <a href={`mailto:${consultant.email}?subject=${encodeURIComponent(opportunity.title)}`} className="mt-4 flex items-center gap-2 text-sm text-copper-deep [overflow-wrap:anywhere] hover:underline" data-id="element-981" data-mp="1f1plp7g77wdv">
                  <MailIcon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-982" data-mp="15a3cthrc29ea" />
                  {consultant.name} — {consultant.email}
                </a>}
            </div>
          </aside>
        </div>
      </section>
    </>;
}